import { morph, MORPH_OPTIONS } from '@theme/morph';

class SectionRenderer {

  #cache = new Map();

  #abortControllersBySectionId = new Map();

  #pendingRendersBySectionId = new Map();

  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
    window.addEventListener('pageshow', this.#onPageShow);
  }

  #onPageShow = (event) => {
    if (event.persisted) this.#cache.clear();
  };

  get cacheStats() {
    let chars = 0;
    for (const html of this.#cache.values()) {
      chars += typeof html === 'string' ? html.length : 0;
    }

    return { count: this.#cache.size, chars };
  }

  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full', injectStylesheet = false } = options ?? {};
    const { url, cartCommitToken, shouldRender } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const renderPromise = this.#renderSection(
      sectionId,
      { cache, mode, injectStylesheet, url, cartCommitToken, shouldRender },
      abortController
    );
    this.#pendingRendersBySectionId.set(sectionId, { abortController, promise: renderPromise });

    return renderPromise;
  }

  abortRender(sectionId) {
    this.#abortPendingMorph(sectionId);
  }

  async #renderSection(sectionId, { cache, mode, injectStylesheet, url, cartCommitToken, shouldRender }, abortController) {
    let sectionHTML = '';

    try {
      sectionHTML = await this.getSectionHTML(sectionId, cache, url, abortController.signal);
    } catch (error) {
      if (abortController.signal.aborted) {
        const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
        if (pendingRender && pendingRender.abortController !== abortController) {
          return pendingRender.promise;
        }

        return sectionHTML;
      }

      throw error;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }

      const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
      if (pendingRender?.abortController === abortController) {
        this.#pendingRendersBySectionId.delete(sectionId);
      }
    }

    if (!abortController.signal.aborted && (shouldRender?.(sectionHTML) ?? true)) {
      await morphSection(sectionId, sectionHTML, { mode, injectStylesheet, cartCommitToken });
    }

    return sectionHTML;
  }

  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
      this.#abortControllersBySectionId.delete(sectionId);
    }
  }

  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), signal) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    const shouldSharePendingPromise = !signal && useCache;
    if (shouldSharePendingPromise) {
      const pendingPromise = this.#pendingPromises.get(sectionUrl);
      if (pendingPromise) return pendingPromise;
    }

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    let pendingPromise = fetch(sectionUrl, { signal }).then((response) => {

      if (!response.ok) throw new Error(`Section ${sectionId} request failed: HTTP ${response.status}`);
      return response.text();
    });

    if (shouldSharePendingPromise) {
      pendingPromise = pendingPromise.finally(() => {
        this.#pendingPromises.delete(sectionUrl);
      });

      this.#pendingPromises.set(sectionUrl, pendingPromise);
    }

    const sectionHTML = await pendingPromise;

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);

      if (this.#cache.has(url) || containsShadowRoot(section)) continue;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

function containsShadowRoot(element) {
  if (element.shadowRoot) return true;

  for (const child of element.children) {
    if (containsShadowRoot(child)) return true;
  }

  return false;
}

const CART_REVISION_ATTRIBUTE = 'data-cg-cart-revision';

let cartCommitCounter = 0;

export function nextCartCommitToken() {
  cartCommitCounter += 1;
  return cartCommitCounter;
}

export function cartCommitTokenFor() {
  return cartCommitCounter;
}

const cartCommitTokensByKey = new WeakMap();

export function claimCartCommitToken(key) {
  const token = nextCartCommitToken();
  if (key && typeof key === 'object') cartCommitTokensByKey.set(key, token);
  return token;
}

export function cartCommitTokenOf(key) {
  if (!key || typeof key !== 'object') return null;
  return cartCommitTokensByKey.get(key) ?? null;
}

export function readSurfaceRevision(element) {
  const raw = element?.getAttribute?.(CART_REVISION_ATTRIBUTE);
  if (raw == null) return null;

  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
}

export function stampSurfaceRevision(element, token) {
  element?.setAttribute?.(CART_REVISION_ATTRIBUTE, String(token));
}

export function canCommitCartWrite(element, token) {
  if (token == null) return true;

  const recorded = readSurfaceRevision(element);
  if (recorded == null) return true;

  return token >= recorded;
}

const DEFAULT_RENDER_DEADLINE_MS = 10_000;

const cartRenderWorkByKey = new WeakMap();

export function registerCartRenderWork(key, work) {
  if (!key || typeof key !== 'object') return;
  if (!work || typeof work.then !== 'function') return;

  const pending = cartRenderWorkByKey.get(key) ?? [];
  pending.push(work);
  cartRenderWorkByKey.set(key, pending);
}

export async function cartRenderWorkSettled(key, options = {}) {
  const { deadlineMs = DEFAULT_RENDER_DEADLINE_MS } = options;
  const pending = (key && typeof key === 'object' && cartRenderWorkByKey.get(key)) || [];

  if (pending.length === 0) return { timedOut: false, failed: false };

  let failed = false;
  const observed = Promise.all(
    pending.map((work) =>
      work.then(
        () => undefined,
        () => {
          failed = true;
        }
      )
    )
  );

  let timer;
  const expiry = new Promise((resolve) => {
    timer = setTimeout(() => resolve('timeout'), deadlineMs);
  });

  try {
    const outcome = await Promise.race([observed.then(() => 'settled'), expiry]);
    return { timedOut: outcome === 'timeout', failed: outcome === 'timeout' ? false : failed };
  } finally {
    clearTimeout(timer);
  }
}

export function operationToken(operation) {
  if (!operation || typeof operation.then !== 'function') {
    throw new TypeError('Expected the original cart event.promise');
  }
  return cartCommitTokenOf(operation) ?? claimCartCommitToken(operation);
}

export function trackCartRender(operation, render) {
  if (!operation || typeof operation.then !== 'function') {
    throw new TypeError('Expected the original cart event.promise');
  }
  let dataFulfilled = false;
  const work = operation.then((data) => {
    dataFulfilled = true;
    return render(data);
  });

  const ledgerWork = work.catch((error) => {
    if (!dataFulfilled || error?.name === 'AbortError') return;
    throw error;
  });

  void ledgerWork.catch(() => {});
  registerCartRenderWork(operation, ledgerWork);
  return work;
}

const cartDomWriteHooks = new Set();

export function registerCartDomWriteHooks(hooks) {
  if (!hooks || typeof hooks !== 'object') return () => {};

  cartDomWriteHooks.add(hooks);
  return () => cartDomWriteHooks.delete(hooks);
}

function callCartDomWriteHook(hooks, name, context) {
  try {
    hooks[name]?.(context);
  } catch (error) {
    console.error(`[cart-dom-write] ${name} hook failed:`, error);
  }
}

export function runCartDomWrite(context, write) {
  for (const hooks of cartDomWriteHooks) callCartDomWriteHook(hooks, 'beforeWrite', context);

  try {
    return write();
  } finally {
    for (const hooks of cartDomWriteHooks) callCartDomWriteHook(hooks, 'afterWrite', context);
  }
}

export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false, cartCommitToken } = options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  if (!canCommitCartWrite(existingElement, cartCommitToken)) return false;

  const commit = () => {
    morph(existingElement, newElement, {
      ...MORPH_OPTIONS,
      hydrationMode: mode === 'hydration',
    });

    if (cartCommitToken != null) stampSurfaceRevision(existingElement, cartCommitToken);

    if (injectStylesheet) {
      injectSectionStylesheet(fragment, existingElement);
    }
  };

  if (cartCommitToken == null) {
    commit();
  } else {
    runCartDomWrite({ currentElement: existingElement, incomingContent: newElement }, commit);
  }

  return true;
}

function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
